import {
  KalturaAssetType,
  KalturaBookmarkActionType,
  KalturaBookmarkPlayerData,
} from '@kaltura-ott/ott-client-types/api/types';
import { addBookmark, getAssetHistoryNextEpisode, KuxDms } from '@kaltura-ott/tvpil';
import { action, computed } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { singleton } from 'tsyringe';

import type { BingeWatchingSettings } from '../category';
import { PlayableAsset } from '../entities/PlayableAsset';

interface BingeWatchingDms {
  enableNextEpisode: boolean;
  enableBingeWatchingUi: boolean;
  enableAutomaticNextEpisode: boolean;
  automaticNextEpisodeStartTime: number;
  nextEpisodeMaxCountdownTime: number;
  skipIntroMaxCountdownTime: number;
  skipRecapMaxCountdownTime: number;
}

@singleton()
export class BingeWatchingRepository {
  private bingeWatching: BingeWatchingDms;

  constructor(dms: KuxDms) {
    this.bingeWatching = dms.bingeWatching;
  }

  @action
  async getNextEpisode(asset: PlayableAsset, duration: number): Promise<number> {
    await lastValueFrom(
      addBookmark(
        asset.id.toString(),
        duration,
        new KalturaBookmarkPlayerData({
          action: KalturaBookmarkActionType.finish,
          fileId: asset.mediaFileId,
        }),
        KalturaAssetType.media,
      ),
    );

    // @ts-expect-error TODO Fix
    return await getAssetHistoryNextEpisode(String(asset.data.metas.SeriesID));
  }

  smartNextEpisode(asset: PlayableAsset): boolean {
    return asset.creditStartOffset ? asset.creditStartOffset > 0 && this.bingeWatching.enableBingeWatchingUi : false;
  }

  autoNextEpisode(asset: PlayableAsset): boolean {
    return (
      this.bingeWatching.enableBingeWatchingUi &&
      this.bingeWatching.enableAutomaticNextEpisode &&
      !asset.creditStartOffset
    );
  }

  silentNextEpisode(asset: PlayableAsset): boolean {
    return (
      (!this.bingeWatching.enableBingeWatchingUi && asset.creditStartOffset != -1) ||
      (this.bingeWatching.enableBingeWatchingUi &&
        !this.bingeWatching.enableAutomaticNextEpisode &&
        !asset.creditStartOffset)
    );
  }

  showNextButtonTime(asset: PlayableAsset, duration: number): number | undefined {
    if (this.smartNextEpisode(asset)) {
      return asset.creditStartOffset;
    } else if (this.autoNextEpisode(asset)) {
      return duration - this.bingeWatching.automaticNextEpisodeStartTime;
    }
  }

  @computed get showNextButtonDuration(): number {
    return this.bingeWatching.nextEpisodeMaxCountdownTime;
  }

  returnSeriesPage(asset: PlayableAsset): boolean {
    return asset.creditStartOffset === -1 || !this.bingeWatching.enableNextEpisode;
  }

  startTime(
    asset: PlayableAsset,
    currentTime: number,
    duration: number,
    bingeWatchingSettings?: BingeWatchingSettings,
  ): boolean {
    const smart = this.smartNextEpisode(asset);
    const auto = this.autoNextEpisode(asset);

    const smartStartTime =
      this.smartNextEpisode(asset) &&
      (asset.creditStartOffset && asset.creditStartOffset > -1 ? currentTime >= asset.creditStartOffset : false) &&
      bingeWatchingSettings?.watchNow;
    const autoStartTime =
      auto &&
      currentTime >= duration - this.bingeWatching.automaticNextEpisodeStartTime &&
      bingeWatchingSettings?.watchNow;
    const silentStartTime = this.silentNextEpisode(asset) && currentTime >= duration;
    const withCreditsStartTime = smart && Boolean(bingeWatchingSettings?.watchCredits) && currentTime >= duration;
    const watchNowStartTime = (smart || auto) && Boolean(bingeWatchingSettings?.watchNow);

    return smartStartTime || autoStartTime || silentStartTime || withCreditsStartTime || watchNowStartTime;
  }
}
